/**
 * Export Calendar Modal
 * Allows users to select which assignments to export to ICS format
 */

import { useState, useMemo } from 'react'
import { X, Download, Calendar, Clock, MapPin, Check, Loader2, Info } from 'lucide-react'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import { supabase } from '../lib/supabase'
import { generateICS, downloadICS, type ICSEvent } from '../services/icsExport'
import { getTgWorkStatusName, getTgWorkStatusColor } from '../services/statusSystemV2'
import type { TgWorkStatus } from '../types/statusTypes'

interface Assignment {
  id: string
  assignment_number: number | null
  title: string
  description: string | null
  start_date: string
  start_time: string | null
  end_time: string | null
  status: string
  tg_status: string | null
  customer_name: string
  customer_phone: string | null
  location: string | null
}

interface ExportCalendarModalProps {
  isOpen: boolean
  onClose: () => void
  assignments: Assignment[]
  monthLabel: string
  currentSubcontractorId?: string
}

export function ExportCalendarModal({
  isOpen,
  onClose,
  assignments,
  monthLabel,
  currentSubcontractorId
}: ExportCalendarModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(assignments.map(a => a.id)))
  const [isExporting, setIsExporting] = useState(false)

  // Group assignments by date
  const groupedAssignments = useMemo(() => {
    const groups: Record<string, Assignment[]> = {}
    for (const assignment of assignments) {
      if (!groups[assignment.start_date]) {
        groups[assignment.start_date] = []
      }
      groups[assignment.start_date].push(assignment)
    }
    // Sort by date
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
  }, [assignments])

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const selectAll = () => {
    setSelectedIds(new Set(assignments.map(a => a.id)))
  }

  const selectNone = () => {
    setSelectedIds(new Set())
  }

  const handleExport = async () => {
    setIsExporting(true)

    try {
      const selectedAssignments = assignments.filter(a => selectedIds.has(a.id))
      const assignmentIds = selectedAssignments.map(a => a.id)

      // Fetch coworkers for all selected assignments
      const coworkersMap: Record<string, { name: string; phone: string | null }[]> = {}

      if (currentSubcontractorId && assignmentIds.length > 0) {
        const { data: coworkersData } = await supabase
          .from('assignment_subcontractors')
          .select(`
            assignment_id,
            subcontractors (name, phone)
          `)
          .in('assignment_id', assignmentIds)
          .neq('subcontractor_id', currentSubcontractorId)
          .neq('tg_status', 'avsagt')

        if (coworkersData) {
          for (const row of coworkersData) {
            const sub = row.subcontractors as any
            if (sub) {
              if (!coworkersMap[row.assignment_id]) {
                coworkersMap[row.assignment_id] = []
              }
              coworkersMap[row.assignment_id].push({
                name: sub.name,
                phone: sub.phone
              })
            }
          }
        }
      }

      // Get base URL for assignment links
      const baseUrl = window.location.origin

      const events: ICSEvent[] = selectedAssignments.map(a => {
        // Build description with clean formatting
        const lines: string[] = []

        // Header with assignment number and title
        const headerText = a.assignment_number
          ? `#${a.assignment_number} - ${a.title}`
          : a.title
        lines.push(`📋 ${headerText}`)

        // Customer info
        if (a.customer_name || a.customer_phone || a.location) {
          lines.push('─'.repeat(30))
          if (a.customer_name) {
            lines.push(`👤 ${a.customer_name}`)
          }
          if (a.customer_phone) {
            lines.push(`📞 ${a.customer_phone}`)
          }
          if (a.location) {
            lines.push(`📍 ${a.location}`)
            const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(a.location)}`
            lines.push(`🧭 ${mapsUrl}`)
          }
        }

        // Coworkers
        const coworkers = coworkersMap[a.id] || []
        if (coworkers.length > 0) {
          lines.push('─'.repeat(30))
          lines.push('🌿 Kollegor:')
          for (const cw of coworkers) {
            const cwLine = cw.phone ? `   ${cw.name} - ${cw.phone}` : `   ${cw.name}`
            lines.push(cwLine)
          }
        }

        // Description
        if (a.description) {
          lines.push('─'.repeat(30))
          lines.push(a.description.trim())
        }

        // Link to assignment
        lines.push('─'.repeat(30))
        lines.push(`🔗 Öppna i appen:`)
        lines.push(`${baseUrl}/subcontractor/assignments/${a.id}`)

        // Build title with assignment number
        const title = a.assignment_number
          ? `#${a.assignment_number} ${a.title}`
          : a.title

        return {
          id: a.id,
          title,
          start_date: a.start_date,
          start_time: a.start_time,
          end_time: a.end_time,
          location: a.location,
          description: lines.join('\n')
        }
      })

      const icsContent = generateICS(events, `TSA Uppdrag - ${monthLabel}`)
      const filename = `uppdrag-${monthLabel.toLowerCase().replace(/\s+/g, '-')}.ics`
      downloadICS(icsContent, filename)

      onClose()
    } finally {
      setIsExporting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white dark:bg-nature-800 rounded-xl shadow-xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-nature-200 dark:border-nature-700">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
              Exportera till kalender
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors"
          >
            <X className="h-5 w-5 text-nature-500" />
          </button>
        </div>

        {/* Info box */}
        <div className="px-4 py-3 border-b border-nature-200 dark:border-nature-700 bg-blue-50 dark:bg-blue-900/20">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Så här fungerar det:</p>
              <ol className="list-decimal list-inside space-y-0.5 text-blue-700 dark:text-blue-300">
                <li>Välj vilka uppdrag du vill exportera</li>
                <li>Klicka på "Exportera" för att ladda ner en kalenderfil (.ics)</li>
                <li>Öppna filen för att importera till din kalender (Google, Outlook, Apple m.fl.)</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Selection controls */}
        <div className="px-4 py-2 border-b border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900/50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-nature-600 dark:text-nature-400">
              {selectedIds.size} av {assignments.length} valda
            </span>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="px-2 py-1 text-xs rounded bg-nature-200 dark:bg-nature-700 text-nature-700 dark:text-nature-300 hover:bg-nature-300 dark:hover:bg-nature-600 transition-colors"
              >
                Välj alla
              </button>
              <button
                onClick={selectNone}
                className="px-2 py-1 text-xs rounded bg-nature-200 dark:bg-nature-700 text-nature-700 dark:text-nature-300 hover:bg-nature-300 dark:hover:bg-nature-600 transition-colors"
              >
                Avmarkera alla
              </button>
            </div>
          </div>
        </div>

        {/* Assignment list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {assignments.length === 0 ? (
            <p className="text-center text-nature-500 dark:text-nature-400 py-8">
              Inga uppdrag att exportera
            </p>
          ) : (
            groupedAssignments.map(([date, dateAssignments]) => (
              <div key={date}>
                {/* Date header */}
                <h3 className="text-sm font-medium text-nature-700 dark:text-nature-300 mb-2 capitalize">
                  {format(new Date(date), 'EEEE', { locale: sv })} {date}
                </h3>

                {/* Assignments for this date */}
                <div className="space-y-2">
                  {dateAssignments.map(assignment => {
                    const isSelected = selectedIds.has(assignment.id)
                    return (
                      <button
                        key={assignment.id}
                        onClick={() => toggleSelect(assignment.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-nature-200 dark:border-nature-700 hover:bg-nature-50 dark:hover:bg-nature-700/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <div className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-colors ${
                            isSelected
                              ? 'bg-primary-600 border-primary-600'
                              : 'border-nature-300 dark:border-nature-600'
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </div>

                          {/* Assignment info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="font-medium text-nature-900 dark:text-nature-100 truncate">
                                {assignment.assignment_number && (
                                  <span className="text-nature-500 dark:text-nature-400 mr-1">
                                    #{assignment.assignment_number}
                                  </span>
                                )}
                                {assignment.title}
                              </h4>
                              <span
                                className="flex-shrink-0 px-2 py-0.5 text-xs rounded-full"
                                style={{
                                  backgroundColor: `${getTgWorkStatusColor(assignment.tg_status as TgWorkStatus)}20`,
                                  color: getTgWorkStatusColor(assignment.tg_status as TgWorkStatus)
                                }}
                              >
                                {getTgWorkStatusName(assignment.tg_status as TgWorkStatus)}
                              </span>
                            </div>

                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-nature-500 dark:text-nature-400">
                              {assignment.start_time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {assignment.start_time.slice(0, 5)}
                                  {assignment.end_time && ` - ${assignment.end_time.slice(0, 5)}`}
                                </span>
                              )}
                              {assignment.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  <span className="truncate max-w-[150px]">{assignment.location}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-nature-200 dark:border-nature-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-nature-300 dark:border-nature-600 text-nature-700 dark:text-nature-300 hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors"
          >
            Avbryt
          </button>
          <button
            onClick={handleExport}
            disabled={selectedIds.size === 0 || isExporting}
            className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isExporting ? 'Exporterar...' : `Exportera ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}
